<script setup>
import eventsQuery from '~/assets/gql/events-filtering.gql';
import collectionQuery from '~/assets/gql/collection.gql';

const setI18nParams = useSetI18nParams();
const { locale, t } = useI18n();
const localePath = useLocalePath();
const route = useRoute();

const props = defineProps(['buildings', 'tags', 'eventCategories']);

const elementsRefs = ref([]);
const elementsVisibility = ref([]);
let observer = false;

onMounted(async () => {
  await nextTick();

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const index = elementsRefs.value.indexOf(entry.target);
        if (index !== -1 && entry.isIntersecting) {
          elementsVisibility.value[index] = true;
        }
      });
    },
    { threshold: 0.1 }
  );

  elementsRefs.value.forEach((el) => {
    if (el) observer.observe(el);
  });
});

onUnmounted(() => {
  if (observer) {
    observer.disconnect();
  }
});

//store
const store = useEventsStore();
const activeDropDownItem = computed(() => store.activeDropDownItem);
const activeTabIndex = computed(() => store.activeTabIndex);
const activeTabItem = computed(() => store.activeTabItem);
const activeTabByDate = computed(() => store.activeTabByDate);
const activeTabByDateString = computed(() => store.activeTabByDateString);
const { setDropDownItem, setActiveTabItem, setActiveTabIndex, setActiveTabByDate, setActiveTabByDateString } = store;

// filtering
const limitAdd = 9;
// const collectionLimit = ref(limitAdd);

const initialLimit = ref(limitAdd);
const initialEvents = ref([]);
const initialHasMoreEvents = ref(false);

const filteredLimit = ref(limitAdd);
const filteredEvents = ref([]);
const filteredHasMoreEvents = ref(false);

const filterQuery = ref('');
const tabs = ref(null);

const categories = [
  { name: t('global.all'), slug: { en: 'all', cs: 'vse' }, eventTypes: ['event'] },
  { name: t('pages.events.exhibitions-all-title'), slug: { en: 'exhibitions', cs: 'vystavy' }, eventTypes: ['exhibition'] },
  { name: t('pages.events.expositions-title'), slug: { en: 'permanent-exhibitions', cs: 'sbirkove-expozice' }, eventTypes: ['exposition'] },
  ...props.eventCategories,
  ...props.tags,
];

const buildingTab = {
  dropdown: props.buildings,
  name: t('pages.events.building-tab'),
  type: 'building',
};
const datepickerTab = {
  datepicker: true,
  name: t('global.date-range.select-days'),
  type: 'subfilter',
  dates: null,
};

const basicTabs = [
  {
    id: 0,
    name: t('global.date-range.ongoing'),
    // slug: useSlugify(t('global.date-range.ongoing')),
    slug: { en: 'ongoing', cs: 'probihajici' },
    type: 'subfilter',
    filter: null,
  },
  {
    id: 1,
    name: t('global.date-range.today'),
    // slug: useSlugify(t('global.date-range.today')),
    slug: { en: 'today', cs: 'dnes' },
    type: 'subfilter',
    filter: 'TODAY',
  },
  {
    id: 2,
    name: t('global.date-range.tomorrow'),
    //slug: useSlugify(t('global.date-range.tomorrow')),
    slug: { en: 'tomorrow', cs: 'zitra' },
    type: 'subfilter',
    filter: 'TOMORROW',
  },
  {
    id: 3,
    name: t('global.date-range.this-week'),
    //  slug: useSlugify(t('global.date-range.this-week')),
    slug: { en: 'this-week', cs: 'tento-tyden' },
    type: 'subfilter',
    filter: 'THISWEEK',
  },
];

const exhibitionTabs = [
  {
    id: 0,
    name: t('pages.events.current-tab'),
    //slug: useSlugify(t('pages.events.current-tab')),
    slug: { en: 'ongoing', cs: 'probihajici' },
    type: 'subfilter',
    filter: 'EXHIBITIONS:NOW',
  },
  {
    id: 1,
    name: t('pages.events.future-tab'),
    //slug: useSlugify(t('pages.events.future-tab')),
    slug: { en: 'upcoming', cs: 'budouci' },
    type: 'subfilter',
    filter: 'EXHIBITIONS:FUTURE',
  },
  {
    id: 2,
    name: t('pages.events.past-tab'),
    //slug: useSlugify(t('pages.events.past-tab')),
    slug: { en: 'past', cs: 'minule' },
    type: 'subfilter',
    filter: 'EXHIBITIONS:PAST',
  },
];

const returnTabs = () => {
  if (activeTabIndex.value === 1) {
    setActiveTabByDate(exhibitionTabs[0]);
    setActiveTabByDateString(exhibitionTabs[0].filter);
    return [...exhibitionTabs, buildingTab];
  } else if (activeTabIndex.value === 2) {
    setActiveTabByDate(null);
    setActiveTabByDateString(null);
    return [{ ...buildingTab }];
  } else {
    setActiveTabByDate(basicTabs[0]);
    setActiveTabByDateString(basicTabs[0].filter);
    return [...basicTabs, { ...datepickerTab, id: 4 }, buildingTab];
  }
};

const loadMore = () => {
  filteredLimit.value = filteredLimit.value + limitAdd;
};

const loadMoreInitial = () => {
  initialLimit.value = initialLimit.value + limitAdd;
};

const variables = ref({});
const initialVariables = ref({});
watchEffect(() => {
  variables.value.limit = filteredLimit.value || limitAdd;
  variables.value.eventTypes = activeTabItem.value?.eventTypes || ['event'];
  variables.value.filterDate = activeTabByDateString.value || null;
  variables.value.filterTag = activeTabItem.value?.__typename === 'Tag' ? activeTabItem.value.id.toString() : null;
  variables.value.filterCategory = activeTabItem.value?.__typename === 'EventCategory' ? activeTabItem.value.id.toString() : null;
  variables.value.filterSearch = filterQuery.value || '';
  variables.value.filterBuilding = activeDropDownItem.value?.id.toString() || null;
});

watchEffect(() => {
  initialVariables.value.limit = initialLimit.value || limitAdd;
  initialVariables.value.eventTypes = ['exposition', 'exhibition'];
  initialVariables.value.filterDate = activeTabByDateString.value || null;
  initialVariables.value.filterSearch = filterQuery.value || '';
  initialVariables.value.filterBuilding = activeDropDownItem.value?.id.toString() || null;
});

const { result, loading, error, refetch } = useQuery(eventsQuery, variables);
const { result: hpResult, loading: hpLoading } = useQuery(eventsQuery, initialVariables);

watchEffect(() => {
  filteredHasMoreEvents.value = result.value?.events?.has_more_pages;
  filteredEvents.value = result.value?.events?.data;
});

watchEffect(() => {
  initialHasMoreEvents.value = hpResult.value?.events?.has_more_pages;
  initialEvents.value = hpResult.value?.events?.data;
});

//change the limit on filter change
watchEffect(() => {
  filteredLimit.value = limitAdd;
  initialLimit.value = limitAdd;
});

const setFilterBy = (item, key, initial = false) => {
  if (key === 7) {
    setActiveTabItem(Object.assign({}, item, { eventTypes: ['exposition', 'exhibition', 'event'] }));
  } else {
    setActiveTabItem(item);
  }

  setActiveTabIndex(key);
  setDropDownItem(null);
  filteredLimit.value = limitAdd;
  initialLimit.value = limitAdd;
  tabs.value = returnTabs();

  if (initial === false) {
    const currentPath = `/${locale.value === 'en' ? 'en/' : ''}${useSlugify(t('pages.events.breadcrumbs-title'))}`;
    const hasCategory = () => {
      return activeTabItem.value?.slug ? `/${activeTabItem.value?.slug[locale.value]}` : '';
    };
    history.pushState({}, null, currentPath + hasCategory());
  }
};

//stuff in url?
const categoryInUrl = ref(null);
const categoryInUrlIndex = ref(0);
const buildingInUrl = ref(null);
const dateInUrl = ref(null);

if (route.params.category !== undefined) {
  categoryInUrl.value = categories.find((c) => c?.slug[locale.value] === route.params.category);
  categoryInUrlIndex.value = categories.findIndex((category) => category.name == categoryInUrl.value?.name);
  setFilterBy(categoryInUrl.value, categoryInUrlIndex.value, true);
} else {
  setFilterBy(categories[0], 0, true);
}
if (route.params.building !== undefined) {
  buildingInUrl.value = props.buildings.find((b) => b.seo_url_slug[locale.value] === route.params.building);
  setDropDownItem(buildingInUrl.value);
}
if (route.params.time !== undefined) {
  const activeTabs = returnTabs();
  dateInUrl.value = activeTabs.find((t) => t?.slug[locale.value] === route.params.time);
  setActiveTabByDate(dateInUrl.value);
  setActiveTabByDateString(dateInUrl.value.filter);
}

//collect expositions for homepage here
const { data: hpEvents } = await useAsyncQuery(eventsQuery, {
  limit: initialLimit.value,
  eventTypes: ['exhibition', 'exposition'],
  filterDate: activeTabByDateString.value || null,
  filterBuilding: activeDropDownItem.value?.id.toString() || null,
});

watchEffect(() => {
  if (hpEvents.value?.events) {
    initialHasMoreEvents.value = hpEvents.value.events.has_more_pages;
    initialEvents.value = hpEvents.value.events.data;
  }
});

//initialEvents
const { data } = await useAsyncQuery(eventsQuery, {
  limit: filteredLimit.value,
  eventTypes: activeTabItem.value?.eventTypes || ['event'],
  filterDate: activeTabByDateString.value || null,
  filterTag: activeTabItem.value?.__typename === 'Tag' ? activeTabItem.value.id.toString() : null,
  filterCategory: activeTabItem.value?.__typename === 'EventCategory' ? activeTabItem.value.id.toString() : null,
  filterBuilding: activeDropDownItem.value?.id.toString() || null,
});

watchEffect(() => {
  if (data.value?.events) {
    filteredHasMoreEvents.value = data.value?.events?.has_more_pages;
    filteredEvents.value = data.value?.events?.data;
  }
});

// setI18nParams({
//   en: {
//     category: activeTabItem?.value?.slug?.en,
//     time: activeTabByDate?.value?.slug?.en,
//     building: activeDropDownItem?.value?.seo_url_slug.en,
//   },
//   cs: {
//     category: activeTabItem?.value?.slug?.cs,
//     time: activeTabByDate?.value?.slug?.cs,
//     building: activeDropDownItem?.value?.seo_url_slug.cs,
//   },
// });
</script>

<template>
  <div
    :ref="(el) => (elementsRefs[0] = el)"
    v-if="categories"
    class="button-group"
    :class="{ 'visible animate__animated animate__fadeInUp': elementsVisibility[0], invisible: !elementsVisibility[0] }"
  >
    <TagButton v-for="(category, key) in categories" key="key" @click="setFilterBy(category, key)" :active="key === activeTabIndex">
      {{ category?.name }}
    </TagButton>

    <TagButton v-if="locale == 'cs'">
      <nuxt-link :to="localePath('events-podcast')">podcast</nuxt-link>
    </TagButton>
  </div>

  <div v-if="tabs" class="events__tabs">
    <Tabs :items="tabs" />
  </div>

  <div :ref="(el) => (elementsRefs[1] = el)" class="events__search" :class="{ 'visible animate__animated animate__fadeInUp': elementsVisibility[1], invisible: !elementsVisibility[1] }">
    <label for="eventsSearch" class="hidden">
      {{ t('pages.events.search') }}
    </label>
    <input id="eventsSearch" v-model="filterQuery" type="text" class="input input--search" :placeholder="t('pages.events.search')" autocomplete="off" />
  </div>

  <div>
    <!-- HP vse: Vzdělávací a veřejné programy -->
    <div v-if="activeTabIndex === 0">
      <div class="events__heading events__heading-events">
        <h2 class="heading">
          {{ t('pages.events.exhibitions-title') }}
        </h2>
      </div>

      <p v-if="hpLoading">{{ t('global.loading') }}</p>
      <div class="events__content">
        <EventWall v-if="initialEvents?.length > 0 || hpLoading" :data="initialEvents" />
        <p v-else class="mt-5">{{ t('pages.events.no-results') }}</p>
      </div>

      <div v-if="initialHasMoreEvents" class="events__more">
        <a class="button" @click="loadMoreInitial">
          {{ t('pages.events.show-more') }}
        </a>
      </div>
    </div>

    <!-- Filtered events list -->
    <div>
      <div class="events__heading events__heading-events">
        <h2 class="heading">
          {{ activeTabIndex === 0 ? t('pages.events.all-events') : activeTabItem?.name }}
        </h2>
      </div>
      <div v-if="activeTabItem?.description" class="events__description">
        <div v-html="activeTabItem?.description"></div>
      </div>

      <p v-if="loading">{{ t('global.loading') }}</p>
      <div class="events__content">
        <EventWall v-if="filteredEvents?.length > 0 || loading" :data="filteredEvents" />
        <p v-else class="mt-5">{{ t('pages.events.no-results') }}</p>
      </div>

      <div v-if="filteredHasMoreEvents" class="events__more">
        <a class="button" @click="loadMore">
          {{ t('pages.events.show-more') }}
        </a>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.button-group {
  display: flex;
  flex-wrap: wrap;
  margin-right: -6px;
  margin-bottom: -6px;

  &__item,
  .dropdown {
    display: inline-block;
    margin-right: 6px;
    margin-bottom: 6px;
  }
}

.events {
  min-height: 100vh;

  &__close {
    @include mq($sm, true) {
      background: $white;
      height: 40px;
      position: absolute;
      right: 20px;
      top: -90px;
      width: 40px;

      &:before,
      &:after {
        background: $black;
        content: '';
        height: 2px;
        margin-left: -14px;
        position: absolute;
        left: 50%;
        top: 50%;
        width: 28px;
      }
      &:before {
        transform: rotate(45deg);
      }
      &:after {
        transform: rotate(-45deg);
      }
    }
  }

  &__filters {
    margin-bottom: 30px;

    position: relative;
    z-index: 1;

    @include mq($sm, true) {
      background: $white;
      margin-bottom: 0;
      height: 100%;
      height: calc(100vh - 110px);
      -webkit-overflow-scrolling: touch;
      overflow-y: auto;
      position: fixed;
      padding: 20px;
      left: 0;
      top: 110px;
      width: 100%;
      z-index: 3;

      .button {
        margin-top: 20px;
      }

      p {
        margin-top: 20px;
        text-align: center;

        a {
          @include underline-transition-reverse(1px);
        }
      }
    }
  }

  &__filter {
    @include mq($sm, true) {
      &:not(:first-child) {
        margin-top: 20px;
      }

      label {
        font-size: 16px;
        font-weight: $font-weight-medium;
        display: block;
        margin-bottom: 10px;
        text-transform: uppercase;
      }

      select {
        text-transform: uppercase;
      }

      option {
        text-transform: uppercase;
      }
    }
  }

  &__heading {
    margin-bottom: 40px;

    &:not(:first-child) {
      margin-top: 80px;
    }
  }

  &__description {
    margin-bottom: 40px;
    margin-top: -20px;
  }

  &__tabs {
    margin-bottom: 15px;
    margin-top: 20px;
  }

  &__search {
    margin-bottom: 50px;
  }

  &__content {
  }

  &__more {
    margin-top: 40px;
    text-align: center;
  }
}

.events__heading-events {
  margin-top: 80px;
}

.events__description {
  max-width: 1200px;
}
</style>
